import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as yup from 'yup';
import * as Message from '../../../util/messageValidation';
import { usePromptConfirm, useErrorToast } from '../../../hooks';
import {
  Modal,
  EditIcon,
  IconButton,
  TrashIcon,
  ActivePopupMenu,
  Loading,
  QuestionIcon,
  InputText,
  InputDate,
} from '../../../components';
import { fetchSurveyById, updateSurvey } from '../../../stores/actions/management';
import { dateFormat } from '../../../util/formatters/dateFormat';
import * as Ui from './styles';

const schema = yup.object().shape({
  description: yup
    .string()
    .min(5, 'Este campo deve possuir no mínimo 5 caracteres')
    .max(250, '')
    .required(Message.required),
  pesquisaDataInicio: yup.date().typeError('O campo deve possuir uma data válida').required('Este campo é obrigatório'),
  pesquisaDataFinalizacao: yup.date().typeError('O campo deve possuir uma data válida').required('Este campo é obrigatório')
    .min(yup.ref('pesquisaDataInicio', ''), 'A data de finalização deve ser maior que a data de início da pesquisa'),
});

function EditorModal({
  // identificador do atual tenant
  customerId,
  // identificador da pesquisa
  surveyId,
  // propaga o evento de salvar
  onSave,
  // propaga o evento de fechamento do modal
  onClose,
  // propriedades implícitas
  ...props
}) {
  // from redux
  // surveyData = {id, descricao, compartilhamentos = {id, cliente, ativo, is_principal}}
  const { dispatch, running, error, surveyData, customersData, theme } = props;

  // state
  const [internalSurvey, setInternalSurvey] = useState(null);
  const [description, setDescription] = useState(null);
  const [pesquisaDataInicio, setPesquisaDataInicio] = useState();
  const [pesquisaDataFinalizacao, setPesquisaDataFinalizacao] = useState();
  const [usersToRemove, setUsersToRemove] = useState([]);
  const [activations, setActivations] = useState([]);
  const [modalTitle, setModalTitle] = useState('Editar Pesquisa');

  // hooks
  const [confirmSave, renderSaveConfirmation] = usePromptConfirm();
  const [confirmRemove, renderRemovingConfirmation] = usePromptConfirm();
  const [checkError] = useErrorToast();

  // refs
  const formRef = useRef();

  // get customer name like callback
  const getCustomerName = useCallback((cId) => {
    let customerName = 'Não definido';
    customersData.forEach((customer) => {
      if (customer.id === cId) {
        customerName = customer.nome;
      }
    });
    return customerName;
  }, [customersData]);

  // error effect
  useEffect(() => {
    if (error) {
      checkError(error);
    }
  }, [checkError, error]);

  // result of fetch survey data (resp)
  useEffect(() => {
    if (surveyData) {
      const dataInicioParts = surveyData.data_inicio.split('/');
      const dataInicio = new Date(+dataInicioParts[2], dataInicioParts[1] - 1, +dataInicioParts[0]);
      const dataFimParts = surveyData.data_finalizacao.split('/');
      const dataFinalizacao = new Date(+dataFimParts[2], dataFimParts[1] - 1, +dataFimParts[0]);
      setInternalSurvey(surveyData);
      setDescription(surveyData.descricao);
      setPesquisaDataInicio(dataInicio);
      setPesquisaDataFinalizacao(dataFinalizacao);
      setModalTitle(`Editar Avaliação: ${surveyData.id} - ${surveyData.descricao}`);
      formRef.current.setFieldValue(
        'description',
        surveyData.descricao,
      );
      formRef.current.setFieldValue(
        'pesquisaDataInicio',
        dataInicio,
      );
      formRef.current.setFieldValue(
        'pesquisaDataFinalizacao',
        dataInicio,
      );
    }
  }, [surveyData]);

  // initial effect (req)
  useEffect(() => {
    if (surveyId) {
      fetchSurveyById(customerId, surveyId, dispatch);
    }
  }, [customerId, surveyId, dispatch]);

  // handle formik submit
  function handleSubmit(values) {
    if (!formRef.current.isValid) {
      return;
    }
    updateSurvey(
      customerId,
      surveyId,
      description,
      dateFormat(pesquisaDataInicio),
      dateFormat(pesquisaDataFinalizacao),
      usersToRemove,
      activations,
      dispatch,
    );
    // propagate event
    if (onSave) onSave();
  }

  // manipula o acionamento do botão "salvar"
  function handleOk() {
    confirmSave(() => {
      formRef.current.submitForm();
    });
  }

  // manipula o acionamento do botão "cancelar"
  function handleClose() {
    if (onClose) onClose();
  }

  // handle remove customer
  function handleRemoveCustomer(userId) {
    confirmRemove(() => {
      // remove from internal data
      const sel = { ...internalSurvey };
      sel.compartilhamentos = internalSurvey.compartilhamentos.filter((u) => (
        u.id !== userId
      ));
      setInternalSurvey(sel);
      // add to removing
      const uSel = [...usersToRemove, userId];
      setUsersToRemove(uSel);
    });
  }

  // handle sharing activation
  function handleSelectActiveMenuItem(userId, actived) {
    // find in activations and remove from activations if exists
    const filteredSel = activations.filter((a) => a.id === userId);
    // add new to activations
    const sel = [...filteredSel, { id: userId, ativo: actived }];
    setActivations(sel);
    // update shared data
    const newInternalSurvey = {
      ...internalSurvey,
      compartilhamentos: internalSurvey.compartilhamentos.map((s) => {
        if (s.id === userId) {
          return { ...s, ativo: actived };
        }
        return s;
      }),
    };
    setInternalSurvey(newInternalSurvey);
  }

  // handle description
  function handleChangeDescription(text) {
    formRef.current.setFieldValue(
      'description',
      text,
    );
    setDescription(text);
  }

  // altera o valor da data inicial
  function handleChangeInitialDate(date) {
    formRef.current.setFieldValue(
      'pesquisaDataInicio',
      date,
    );
    setPesquisaDataInicio(date);
  }
  // altera o valor da data de finalização
  function handleChangeEndDate(date) {
    formRef.current.setFieldValue(
      'pesquisaDataFinalizacao',
      date,
    );
    setPesquisaDataFinalizacao(date);
  }

  return (
    <Modal
      isOpen
      title={modalTitle}
      okBtnTitle={'Salvar'}
      cancelBtnTitle={'Cancelar'}
      onCancel={() => handleClose()}
      onOk={() => handleOk()}
      iconComponent={(<EditIcon className="icon" color="#610082" size="15" />)}
    >
      <Formik
        onSubmit={handleSubmit}
        innerRef={formRef}
        validationSchema={schema}
        validateOnBlur
        validateOnChange
        initialValues={{
          description,
          pesquisaDataInicio,
          pesquisaDataFinalizacao,
        }}
      >
        {(running || !internalSurvey) ? (
          <Loading size={30} running={running} />
        ) : (
          <Ui.Container>
            <Ui.GroupInputs>
              <Ui.NewTitle>
                <Ui.Label>{'Título da avaliação'}</Ui.Label>
                <InputText
                  onChange={(text) => handleChangeDescription(text)}
                  value={description}
                  background={theme.colors.editSurveyBackgroundColor}
                  defaultValue={description}
                  name="description"
                  maxLength={251}
                  hasError={description.length > 250}
                />
                {description.length > 250 && 
                  <Ui.CustomErrorMessage>
                    {'Limite de 250 caracteres'}
                  </Ui.CustomErrorMessage>
                }
                <ErrorMessage name="description">
                  {(msg) => <span className="errorMessage">{msg}</span>}
                </ErrorMessage>
              </Ui.NewTitle>
            </Ui.GroupInputs>
            <Ui.GroupInputs>
              <Ui.DateContainer>
                <label className="input-label">Data de início *</label>
                <Ui.InputDateContainer>
                  <InputDate
                    placeholder="Informe a data de início da avaliação"
                    value={pesquisaDataInicio}
                    inputName="pesquisaDataInicio"
                    onChangeDate={(date) => handleChangeInitialDate(date)}
                  />
                </Ui.InputDateContainer>
                {/* error message */}
                <ErrorMessage name="pesquisaDataInicio">
                  {(msg) => <span className="errorMessage">{msg}</span>}
                </ErrorMessage>
              </Ui.DateContainer>
              <Ui.DateContainer>
                <label className="input-label">Data de finalização *</label>
                <Ui.InputDateContainer>
                  <InputDate
                    placeholder="Informe a data de finalização da avaliação"
                    value={pesquisaDataFinalizacao}
                    inputName="pesquisaDataFinalizacao"
                    onChangeDate={(date) => handleChangeEndDate(date)}
                  />
                </Ui.InputDateContainer>
                {/* error message */}
                <ErrorMessage name="pesquisaDataFinalizacao">
                  {(msg) => <span className="errorMessage">{msg}</span>}
                </ErrorMessage>
              </Ui.DateContainer>
            </Ui.GroupInputs>
            <Ui.UsersTable>
              <Ui.TableHead>
                <tr>
                  <th>ID</th>
                  <th>DESCRIÇÃO</th>
                  <th>STATUS</th>
                  <th> </th>
                </tr>
              </Ui.TableHead>
              <Ui.TableBody>
                {internalSurvey.compartilhamentos && internalSurvey.compartilhamentos.map((s) => (
                  <tr>
                    <td>{s.id}</td>
                    <td>{getCustomerName(s.cliente)}</td>
                    <td className={s.ativo ? 'status active' : 'status inactive'}>
                      <ActivePopupMenu
                        isActived={s.ativo}
                        className={'popup-selector'}
                        onActive={(actived) => handleSelectActiveMenuItem(s.id, actived)}
                      >
                        {s.ativo ? 'ATIVO' : 'INATIVO'}
                      </ActivePopupMenu>
                    </td>
                    <td>
                      <IconButton
                        iconSize={17}
                        iconComponent={TrashIcon}
                        onClick={() => handleRemoveCustomer(s.id)}
                      />
                    </td>
                  </tr>
                ))}
              </Ui.TableBody>
            </Ui.UsersTable>
          </Ui.Container>
        )}
      </Formik>
      {renderRemovingConfirmation(TrashIcon, 'Deseja realmente remover este cliente?')}
      {renderSaveConfirmation(QuestionIcon, 'Deseja realizar as alterações nesta avaliação')}
    </Modal>
  );
}


/**
 * mapeia a store para as propriedades que serão passadas para este
 * componente.
 *
 * Duas propriedades são extraídas e passadas para o componente:
 * surveys => obtido da pesquisa selecionada
 *
 * @param {*} state
 * @param {*} props
 */
const mapStateToProps = (state) => {
  // get state
  const surveyState = state.management.survey;
  const customersState = state.tenants;
  // result
  return {
    running: surveyState.running,
    error: surveyState.error,
    surveyData: surveyState.payload,
    customersData: customersState.payload,
  };
};

// create component (delegate) and export
export default connect(
  mapStateToProps, null, null, { forwardRef: false },
)(EditorModal);
